import HttpOperationError from 'es/app/business-logic/HttpOperationError';
import FormContextProvider, { IFormContext } from 'es/app/components/forms/FormContext';
import NavigationPrompt from 'es/app/components/forms/NavigationPrompt';
import ValidationErrors from 'es/app/components/forms/ValidationErrors';
import Throbber from 'es/app/components/progress-indicators/Throbber';
import { Formik, FormikConfig, FormikHelpers, FormikValues, useFormikContext } from 'formik';
import { FormikProps } from 'formik/dist/types';
import React, { useCallback, useState } from 'react';
import LogManager from 'es/core/logging/LogManager';

const logger = LogManager.getLogger('FormikForm.tsx');

interface IFormikFormProps {
    isBusy?: boolean;
    busyText?: string;
    skipDirtyCheck?: boolean;
}

type FormikFormProps<TFormValues extends FormikValues, ExtraProps = {}> = FormikConfig<TFormValues> &
    ExtraProps &
    IFormikFormProps &
    Partial<IFormContext<TFormValues>>;

function FormikForm<TFormValues extends FormikValues, ExtraProps = {}>(
    props: FormikFormProps<TFormValues, ExtraProps>
) {
    const { isBusy, busyText, disabled, skipDirtyCheck, children } = props;

    const [submitError, setSubmitError] = useState<HttpOperationError | null>(null);

    function handleSubmit(values: TFormValues, formikHelpers: FormikHelpers<TFormValues>) {
        try {
            props.onSubmit(values, formikHelpers);
        } catch (err) {
            if (err instanceof HttpOperationError) {
                err.handled = true;
            }

            setSubmitError(err as HttpOperationError);
        }
    }

    return (
        <>
            <Throbber text={busyText} visible={isBusy} />

            <Formik {...props} onSubmit={handleSubmit} enableReinitialize>
                {(form: FormikProps<TFormValues>) => (
                    <>
                        <FormContextProvider<TFormValues> form={form} disabled={isBusy || disabled}>
                            <ValidationErrors form={form} submitError={submitError ?? ''} />

                            {(children as (props: FormikProps<TFormValues>) => React.ReactNode)(form)}
                        </FormContextProvider>

                        {!skipDirtyCheck && <NavigationPrompt when={form.dirty} onLoseChanges={form.resetForm} />}
                    </>
                )}
            </Formik>
        </>
    );
}

/**
 * @deprecated Use "useUpdateFieldValue" instead
 */
export function updateFieldValue<TFormValues, K extends keyof TFormValues>(
    form: FormikProps<TFormValues>,
    fieldName: K,
    newValue: TFormValues[K],
    isTouched = true
) {
    if (typeof fieldName !== 'string') {
        return logger.error(`"fieldName" must be a string, but is ${typeof fieldName}`, fieldName);
    }

    // We have to set the field touched BEFORE setting the field value, otherwise Formik triggers validation incorrectly.
    // Let's say you have a text field that you change from 'pants' to 'shorts'.
    //
    //    form.setFieldValue(field.name, 'shorts')  => form values are updated, validation is triggered with 'shorts'
    //    form.setFieldTouched(field.name, true)    => form touched fields are updated, validation is triggered with 'pants'
    //
    form.setFieldTouched(fieldName, isTouched);
    form.setFieldValue(fieldName, newValue);
}

export function useUpdateFieldValue<TFormValues>() {
    const { setFieldValue } = useFormikContext<TFormValues>();

    return useCallback(
        <K extends keyof TFormValues>({
            fieldName,
            fieldValue,
            shouldValidate,
        }: {
            fieldName: K;
            fieldValue: TFormValues[K];
            isTouched?: boolean;
            shouldValidate?: boolean;
        }) => {
            if (typeof fieldName !== 'string') {
                throw new Error('Field name must be a string');
            }
            setFieldValue(fieldName, fieldValue, shouldValidate);
        },
        [setFieldValue]
    );
}

export default FormikForm;
